
import { format } from 'date-fns-tz';
import { addDays, differenceInMinutes, startOfDay } from 'date-fns';

export const TIME_FORMAT_AMPM = 'h:mmaaa';
export const TIME_FORMAT_24H = 'H:mm';
export const MINUTES_IN_A_DAY = 1440;

let localeData = {};
export let timeFormat = TIME_FORMAT_AMPM;
export function setLocale(business) {
    localeData = {
        weekStart: business?.week_start || 1,
        use24HourTime: business?.use_24_hour_time || false,
    };

    timeFormat = localeData.use24HourTime ? TIME_FORMAT_24H : TIME_FORMAT_AMPM;
}

export const ShortDaysOfWeek = [
    'Sun',
    'Mon',
    'Tue',
    'Wed',
    'Thu',
    'Fri',
    'Sat',
];

export function formatTz(timeStr, timezone, pattern) {
    if (!timeStr) {
        return '';
    }

    try {
        return format(new Date(timeStr), pattern, { timeZone: timezone });
    } catch (e) {
        console.error('Handled formatTz exception: ', e);
        return '';
    }
}

export function formatDateTime(timeStr, timezone) {
    return formatTz(timeStr, timezone, `LLL d ${timeFormat}`);
}

export function formatDateTimeLong(timeStr, timezone) {
    return (
        formatDateLong(timeStr, timezone) + ' ' + formatTime(timeStr, timezone)
    );
}

export function formatTime(timeStr, timezone) {
    return formatTz(timeStr, timezone, timeFormat);
}

export function formatDate(timeStr, timezone) {
    if (new Date(timeStr).getFullYear() !== new Date().getFullYear()) {
        return formatTz(timeStr, timezone, 'LLL d, y');
    } else {
        return formatTz(timeStr, timezone, 'LLL d');
    }
}

export function formatInternalDate(timeStr, timezone) {
    return formatTz(timeStr, timezone, 'yyyy-MM-dd');
}

export function formatInternalTime(timeStr, timezone) {
    return formatTz(timeStr, timezone, 'HH:mm');
}

/**
 * Take a date string like YYYY-MM-DD and convert it to a time that follows timezone
 * @param dateStrYYYYmmDD
 */
export function dateStringToDateTime(dateStrYYYYmmDD) {
    if (!dateStrYYYYmmDD) {
        return null;
    }
    const parts = dateStrYYYYmmDD.split('-');
    const d = new Date();
    d.setHours(0);
    d.setMinutes(0);
    d.setSeconds(0);
    d.setMilliseconds(0);
    d.setFullYear(parts[0]);
    d.setDate(1);
    d.setMonth(parts[1] - 1);
    d.setDate(parts[2]);

    return d;
}

export function formatInternalDateTime(timeStr, timezone) {
    return (
        formatInternalDate(timeStr, timezone) +
        ' ' +
        formatInternalTime(timeStr, timezone)
    );
}

export function formatDateLong(timeStr, timezone) {
    if (new Date(timeStr).getFullYear() !== new Date().getFullYear()) {
        return formatTz(timeStr, timezone, 'EEEE, LLLL d, y');
    } else {
        return formatTz(timeStr, timezone, 'EEEE, LLLL d');
    }
}

export function formatDateShort(timeStr, timezone) {
    if (new Date(timeStr).getFullYear() !== new Date().getFullYear()) {
        return formatTz(timeStr, timezone, 'LLL d, y');
    } else {
        return formatTz(timeStr, timezone, 'LLL d');
    }
}

export function printHoursSet(hoursSet) {
    const start = formatTime(parseTime(hoursSet.start));
    const end = formatTime(parseTime(hoursSet.end));
    return `${start}-${end}`;
}

function getUnit(unit, amount, short) {
    if (!short) {
        return ` ${unit}${amount !== 1 ? 's' : ''}`;
    }

    if (unit === 'minute') {
        return `min`;
    } else if (unit === 'day') {
        return 'd';
    } else if (unit === 'hour') {
        return 'hr';
    } else {
        return unit;
    }
}

export function minutesToDuration(durationInMinutes, short) {
    const days = Math.floor(durationInMinutes / (60 * 24));
    const hours = Math.floor((durationInMinutes % (60 * 24)) / 60);
    const minutes = durationInMinutes % 60;

    if (parseInt(durationInMinutes) === 0) {
        return '0' + getUnit('minute', 0, short);
    }

    let ret = '';
    if (days) {
        ret = `${ret ? ' ' : ''}${days}${getUnit('day', days, short)}`;
    }

    if (hours) {
        ret += `${ret ? ' ' : ''}${hours}${getUnit('hour', hours, short)}`;
    }

    if (minutes) {
        if (ret) {
            ret += ` ${minutes}m`;
        } else {
            ret += `${minutes}${getUnit('minute', minutes, short)}`;
        }
    }

    return ret;
}

export function humanDaysOfWeek(days) {
    days.sort();
    if (days.length === 2 && days === [0, 6]) {
        return 'weekends';
    } else if (days.length === 5 && days === [1, 2, 3, 4, 5]) {
        return 'weekdays';
    } else {
        //const sunday = new Date('2024-08-11');
        const sunday = new Date(2024, 7 /*August*/, 11);
        return days
            .map(d => {
                console.log(
                    'adding ',
                    d,
                    'days to',
                    sunday,
                    '=',
                    addDays(sunday, d),
                    format(addDays(sunday, d), 'EEEE'),
                );
                return format(addDays(sunday, d), 'EEEE');
            })
            .join(', ');
    }
}

/**
 * Parse a string that's just a time without a date
 * @param timeStr
 */
export function parseTime(timeStr) {
    if (!timeStr) {
        return false;
    }

    timeStr = timeStr.toLowerCase().replace(' ', '');
    const parts = timeStr.split(':', 2);
    let hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);

    if (timeStr.indexOf('am') > -1 && hours === 12) {
        hours = 0;
    } else if (timeStr.indexOf('pm') > -1 && hours !== 12) {
        hours += 12;
    }

    const ret = new Date();
    ret.setHours(hours);
    ret.setMinutes(minutes);

    return ret;
}

export function getBrowserTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function minutesAfterDayStart(date) {
    return differenceInMinutes(date, startOfDay(date));
}

export function weekdayOfMonth(date) {
    // Ex., return "3rd Thursday" for August 15, 2024
    const d = new Date(date);
    const dayOfWeek = format(d, 'EEEE'); // e.g., "Tuesday"
    const dayOfMonth = d.getDate();

    // Calculate which occurrence of this weekday in the month (1st, 2nd, 3rd, etc.)
    const occurrence = Math.ceil(dayOfMonth / 7);

    // Get ordinal suffix
    const getOrdinal = (n) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    return `${getOrdinal(occurrence)} ${dayOfWeek}`;
}

export function formatDateFull(timeStr, timezone) {
    return formatTz(timeStr, timezone, 'LLLL d, y');
}
